import {useEffect, useState} from 'react';
import {Grid, MenuItem} from '@mui/material';
import {ExpansionPanel, InputField} from '@/components/common';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import type {CompanyAnnual} from '@/services/accountService/companyAnnualService';

type Props = {
    annualId: number;
    onChange?: (patch: Partial<CompanyAnnual>) => void;
    initialData?: {
        holdingCo?: number;
        nameOfImmediate?: string | null;
        nameOfUltimate?: string | null;
    } | null;
};

export default function EpHoldingStructure({annualId, onChange, initialData}: Props) {
    const store = useCompanyAnnualStore();
    const [local, setLocal] = useState({
        holdingCo: 0,
        nameOfImmediate: null as string | null,
        immediateHolding: null as string | null,
        nameOfUltimate: null as string | null,
        ultimateHolding: null as string | null,
    });

    useEffect(() => {
        if (initialData) {
            setLocal(prev => ({
                ...prev,
                holdingCo: initialData.holdingCo ?? prev.holdingCo,
                nameOfImmediate: initialData.nameOfImmediate ?? prev.nameOfImmediate,
                nameOfUltimate: initialData.nameOfUltimate ?? prev.nameOfUltimate,
            }));
            return;
        }

        let mounted = true;
        (async () => {
            const v = await store.getHoldingStructureInfo(annualId);
            if (!mounted) return;
            setLocal(v);
        })();
        return () => {
            mounted = false;
        };
    }, [annualId, store, initialData]);

    const emit = (patch: Partial<typeof local>) => {
        const next = {...local, ...patch};
        setLocal(next);
        onChange?.(patch as Partial<CompanyAnnual>);
    };

    return (
        <ExpansionPanel title="Holding Structure (控股結構)">
            <Grid container spacing={0.5}>
                <Grid size={12 as any}>
                    <InputField select fullWidth variant="outlined" label="Holding company? (是否有控股公司)"
                                value={local.holdingCo ?? 0}
                                onChange={(e) => emit({holdingCo: Number(e.target.value) as any})}>
                        <MenuItem dense value={0}>No</MenuItem>
                        <MenuItem dense value={1}>Yes</MenuItem>
                    </InputField>
                </Grid>
                {local.holdingCo === 1 && (
                    <>
                        <Grid size={12 as any}>
                            <InputField fullWidth variant="outlined" label="Name of immediate holding co. (直接控股公司名稱)"
                                        value={local.nameOfImmediate || ''}
                                        onChange={(e) => emit({nameOfImmediate: e.target.value || null})}/>
                        </Grid>
                        <Grid size={12 as any}>
                            <InputField fullWidth variant="outlined" label="Immediate holding (place of incoporation) (直接控股公司成立國家)"
                                        value={local.immediateHolding || ''}
                                        onChange={(e) => emit({immediateHolding: e.target.value || null})}/>
                        </Grid>
                        <Grid size={12 as any}>
                            <InputField fullWidth variant="outlined" label="Name of ultimate holding co. (最終控股公司名稱)"
                                        value={local.nameOfUltimate || ''}
                                        onChange={(e) => emit({nameOfUltimate: e.target.value || null})}/>
                        </Grid>
                        <Grid size={12 as any}>
                            <InputField fullWidth variant="outlined" label="Ultimate holding (place of incoporation) (最終控股公司成立國家)"
                                        value={local.ultimateHolding || ''}
                                        onChange={(e) => emit({ultimateHolding: e.target.value || null})}/>
                        </Grid>
                    </>
                )}
            </Grid>
        </ExpansionPanel>
    );
}
